import React from 'react';
import { Button } from '@oysterjs/ui/button';
import { Select } from '@oysterjs/ui/select';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { TriggerEmailRuleKind as Kind, type TriggerEmailRuleInput } from '~/api/graphql';
import { isGroup, newRule, countPrompts, MAX_DEPTH, MAX_NODES } from '~/api/data/triggers/config';
import type { EmailErrors, EmailOptions } from '~/api/data/triggers/email-config';
import { ConditionEditor } from '~/components/triggers/email-builder/condition-editors';
import { BuilderField, PathErrors, Help } from '~/components/triggers/email-builder/fields';

export const CONDITION_LABELS: Record<Kind, string> = {
  AND: 'All (AND)',
  OR: 'Any (OR)',
  NOT: 'Not (NOT)',
  PHRASE: 'Phrase',
  SENDER: 'Sender',
  RECIPIENT: 'To / Cc recipient',
  TIME: 'Time',
  ATTACHMENT: 'Attachment details',
  PROMPT: 'Prompt'
};
export const RuleEditor = ({
  node,
  path,
  depth = 1,
  totalNodes,
  totalPrompts = countPrompts(node),
  errors,
  options,
  onChange,
  onRemove
}: {
  node: TriggerEmailRuleInput;
  path: string;
  depth?: number;
  totalNodes: number;
  totalPrompts?: number;
  errors: EmailErrors;
  options: EmailOptions;
  onChange: (node: TriggerEmailRuleInput) => void;
  onRemove: () => void;
}) => {
  const [addKind, setAddKind] = React.useState(Kind.Phrase);
  const group = isGroup(node.kind),
    children = node.children ?? [];
  const canAdd =
    depth < MAX_DEPTH && totalNodes < MAX_NODES && (node.kind !== Kind.Not || !children.length);
  const kindBlocked = (kind: Kind) =>
    (kind === Kind.Prompt && totalPrompts >= 3) ||
    (isGroup(kind) && (depth + 1 >= MAX_DEPTH || totalNodes + 2 > MAX_NODES));
  return (
    <div
      className={
        group
          ? 'min-w-0 space-y-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-700 dark:bg-neutral-900'
          : 'min-w-0 border-t border-neutral-200 pt-3 dark:border-neutral-700'
      }
      data-rule-id={node.id}
      data-rule-kind={node.kind}
    >
      <div className="mb-3 flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <BuilderField
            label={group ? `Group at level ${depth}` : 'Condition type'}
            path={`${path}.kind`}
            errors={errors}
          >
            {(props) => (
              <Select
                {...props}
                value={node.kind}
                onChange={(event) => {
                  const kind = event.target.value as Kind;
                  onChange(group ? { ...node, kind } : { ...newRule(kind), id: node.id });
                }}
              >
                {Object.values(Kind)
                  .filter((kind) => isGroup(kind) === group)
                  .map((kind) => (
                    <option
                      key={kind}
                      value={kind}
                      disabled={
                        (kind === Kind.Not && children.length > 1) ||
                        (kind === Kind.Prompt && node.kind !== Kind.Prompt && totalPrompts >= 3)
                      }
                    >
                      {CONDITION_LABELS[kind]}
                    </option>
                  ))}
              </Select>
            )}
          </BuilderField>
        </div>
        <Button
          type="button"
          plain
          aria-label={group ? 'Remove group' : 'Remove condition'}
          onClick={onRemove}
        >
          <XMarkIcon />
        </Button>
      </div>
      <PathErrors path={path} errors={errors} />
      {group ? (
        <>
          {node.kind === Kind.Not && (
            <Help>NOT takes one child. Nest an AND or OR group to negate several conditions.</Help>
          )}
          {!children.length && <Help>Add a condition. An empty group cannot be saved.</Help>}
          <div className="space-y-3 sm:ml-3">
            {children.map((child, index) => (
              <RuleEditor
                key={child.id}
                node={child}
                path={`${path}.children.${index}`}
                depth={depth + 1}
                totalNodes={totalNodes}
                totalPrompts={totalPrompts}
                options={options}
                errors={errors}
                onChange={(value) =>
                  onChange({
                    ...node,
                    children: children.map((item, i) => (i === index ? value : item))
                  })
                }
                onRemove={() =>
                  onChange({ ...node, children: children.filter((_, i) => i !== index) })
                }
              />
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <BuilderField
              label="Add a condition or group"
              path={`${path}.children`}
              errors={errors}
            >
              {(props) => (
                <Select
                  {...props}
                  value={addKind}
                  onChange={(event) => setAddKind(event.target.value as Kind)}
                >
                  {Object.values(Kind).map((kind) => (
                    <option key={kind} value={kind} disabled={kindBlocked(kind)}>
                      {CONDITION_LABELS[kind]}
                    </option>
                  ))}
                </Select>
              )}
            </BuilderField>
            <Button
              type="button"
              color="white"
              disabled={!canAdd || kindBlocked(addKind)}
              onClick={() => onChange({ ...node, children: [...children, newRule(addKind)] })}
            >
              Add
            </Button>
          </div>
          {!canAdd && (
            <Help>
              {node.kind === Kind.Not && children.length
                ? 'Remove the existing child before replacing it.'
                : 'Limit: eight levels and 128 nodes.'}
            </Help>
          )}
        </>
      ) : (
        <ConditionEditor
          node={node}
          path={path}
          options={options}
          errors={errors}
          onChange={onChange}
        />
      )}
    </div>
  );
};
